// Constants
const PHASES = [
  "New", "Waxing Crescent", "First Quarter", "Waxing Gibbous",
  "Full", "Waning Gibbous", "Last Quarter", "Waning Crescent"
];

const NORTHERN_EMOJI = ["🌑","🌒","🌓","🌔","🌕","🌖","🌗","🌘"];
const SOUTHERN_EMOJI = ["🌑","🌘","🌗","🌖","🌕","🌔","🌓","🌒"];

const ANOMALISTIC_MONTH = 27.55454988;
const SYNODIC_MONTH     = 29.53058770576;
const EPOCH             = 2440587.5;      // Unix epoch in Julian days
const EPOCH_JULIAN      = 2451545.0;      // 2000-01-01
const EPOCH_SYNODIC     = 2451550.1;      // 2000-01-06 09:24 UTC
const EPOCH_ANOMALISTIC = 2451562.2;      // 2000-01-18 11:48 UTC
const EPOCH_LUNATION    = 2423436.6115277777;
const SECONDS_PER_DAY   = 86400;

// Convert JS Date → Julian Day
export const dateToJulian = (date: Date) => (date.getTime() / 1000 / SECONDS_PER_DAY + EPOCH);
export const julianToDate = (julian: number) => new Date((julian - EPOCH) * SECONDS_PER_DAY * 1000);
export const julianToSynodic = (julian: number) => (julian - EPOCH_SYNODIC) / SYNODIC_MONTH;
export const julianToLunation = (julian: number) => (julian - EPOCH_LUNATION) / SYNODIC_MONTH;
export const lunationToJulian = (lunation: number) => (lunation * SYNODIC_MONTH + EPOCH_LUNATION);
export const synodicToJulian = (synodic: number) => (synodic * SYNODIC_MONTH + EPOCH_SYNODIC);
export const julianToAnomalistic = (julian: number) => (julian - EPOCH_ANOMALISTIC) / ANOMALISTIC_MONTH;
export const percentToPhaseIndex = (p: number) => Math.floor((p * PHASES.length + 0.5) % PHASES.length);
export const dateToSynodic = (date: Date) => julianToSynodic(dateToJulian(date));
export const synodicToDate = (synodic: number) => julianToDate(synodicToJulian(synodic));

// Exported functions

const negmod = (a: number, b=1) => ((a % b) + b) % b;
export const lunarPercent = (date: Date) => { // 0: new, 0.5: full, 1: new
    return negmod(julianToSynodic(dateToJulian(date)), 1);
}

export const lunationNumber = (date: Date) => {
    return Math.round(julianToLunation(dateToJulian(date))) + 1;
}

export const lunationToDate = (lunation: number, percent=0) => {
    return julianToDate(synodicToJulian(lunation - 1 + percent));
}

export const lunarPhaseName = (date: Date) => {
    return PHASES[percentToPhaseIndex(lunarPercent(date))];
}

export const lunarPhaseEmoji = (date: Date, southernHemisphere: boolean = false) => {
    return (southernHemisphere ? SOUTHERN_EMOJI : NORTHERN_EMOJI)[percentToPhaseIndex(lunarPercent(date))];
}

export const lunarDistance = (date: Date) => {
  const julian = dateToJulian(date);
  const synodicPercent = julianToSynodic(julian) % 1;
  const anomalisticPercent = julianToAnomalistic(julian) % 1;

  // Approx. Earth-radius distance formula
  const distance =
    60.4
    - 3.3 * Math.cos(2 * Math.PI * anomalisticPercent)
    - 0.6 * Math.cos(2 * Math.PI * (2 * synodicPercent - anomalisticPercent))
    - 0.5 * Math.cos(2 * Math.PI * 2 * synodicPercent);

  return distance;
}

export const lunarCoordinates = (date: Date) => {
    const julian = dateToJulian(date);
    const daysSince2000 = (julian - EPOCH_JULIAN);
    let moonLongitude = (218.316 + 13.176396 * daysSince2000) % 360;
    const moonAnomaly  = (134.963 + 13.064993 * daysSince2000) % 360;
    const moonLatitude = 5.145 * Math.sin(moonAnomaly * Math.PI / 180);

    // Add evection refinement
    moonLongitude = (moonLongitude + 6.289 * Math.sin(moonAnomaly * Math.PI / 180)) % 360;

    return [moonLongitude, moonLatitude];
}


export const findNextPhase = (from: Date, frac: number): Date | null => {
    const fromSynodic = dateToSynodic(from);
    const fracSynodic = Math.floor(fromSynodic) + frac;
    const nestFracSynodic = fracSynodic <= fromSynodic ? fracSynodic + 1 : fracSynodic;
    // console.log('next phase', frac, from.toISOString(), synodicToDate(fracSynodic).toISOString(), fracSynodic, fromSynodic, nestFracSynodic);
    return synodicToDate(nestFracSynodic);
}


// Main lunar computation
export default function lunar(date: Date) {
  const julian = dateToJulian(date);

  const synodicPercent = julianToSynodic(julian) % 1;
  const anomalisticPercent = julianToAnomalistic(julian) % 1;
  const lunationNumber = Math.round(julianToSynodic(julian)) + 1;
  const phaseIndex = percentToPhaseIndex(synodicPercent);
  const phase = PHASES[phaseIndex];

  // Approx. Earth-radius distance formula
  const distance =
    60.4
    - 3.3 * Math.cos(2 * Math.PI * anomalisticPercent)
    - 0.6 * Math.cos(2 * Math.PI * (2 * synodicPercent - anomalisticPercent))
    - 0.5 * Math.cos(2 * Math.PI * 2 * synodicPercent);

  // Simplified lunar longitude / latitude
  const daysSince2000 = (julian - EPOCH_JULIAN);
  let moonLongitude = (218.316 + 13.176396 * daysSince2000) % 360;
  const moonAnomaly  = (134.963 + 13.064993 * daysSince2000) % 360;
  const moonLatitude = 5.145 * Math.sin(moonAnomaly * Math.PI / 180);

  // Add evection refinement
  moonLongitude = (moonLongitude + 6.289 * Math.sin(moonAnomaly * Math.PI / 180)) % 360;

  return {
    lunar_phase: phase,
    lunar_percent: synodicPercent,
    lunar_anomalistic_percent: anomalisticPercent,
    lunation_number: lunationNumber,
    lunar_distance: distance,
    moon_waxing: synodicPercent <= 0.5,
    moon_latitude: moonLatitude,
    moon_longitude: moonLongitude
  };
}